import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SelectMenu } from '@/components/ui/select-menu';
import { useToast } from '@/components/ui/use-toast';
import { EyeOff } from 'lucide-react';
import StaffAPI from '@/api/staff.api';
import { listOrganizations } from '@/api/organization.api.private';
import { USER_PERMISSIONS } from 'shared/constants/auth.constants';

const PERMISSION_OPTIONS = Object.values(USER_PERMISSIONS);

export default function AddSubscriptionDialog({
  open,
  onOpenChange,
  userId,
  userEmail,
  existingOrgIds = [],
  onSuccess,
}) {
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [organizationId, setOrganizationId] = useState('');
  const [permissions, setPermissions] = useState(USER_PERMISSIONS.VIEWER);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!open) {
      setOrganizationId('');
      setPermissions(USER_PERMISSIONS.VIEWER);
      return;
    }

    let cancelled = false;
    setOrgsLoading(true);
    listOrganizations()
      .then(res => {
        if (cancelled) return;
        if (res.error) {
          toast({
            title: 'Failed to load organizations',
            description: res.error.message || 'Unexpected error',
            variant: 'destructive',
          });
          setOrganizations([]);
          return;
        }
        setOrganizations(Array.isArray(res.data) ? res.data : []);
      })
      .finally(() => {
        if (!cancelled) setOrgsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, toast]);

  const existingSet = useMemo(() => new Set(existingOrgIds.map(String)), [existingOrgIds]);

  const orgOptions = useMemo(() => {
    return organizations
      .filter(org => !existingSet.has(String(org.id ?? org._id)))
      .map(org => ({
        value: String(org.id ?? org._id),
        label: org.name || `(${org.id ?? org._id})`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [organizations, existingSet]);

  const onSubmit = async e => {
    e.preventDefault();
    if (!organizationId || !permissions) return;

    setSubmitting(true);
    try {
      const res = await StaffAPI.createSubscription({
        userId,
        organizationId,
        permissions,
      });

      if (res.error) {
        toast({
          title: 'Failed to create subscription',
          description: res.data?.message || res.error.message || 'Unexpected error',
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Subscription created',
        description: `${permissions} access added (hidden).`,
      });
      onSuccess?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add subscription</DialogTitle>
          <DialogDescription>{userEmail}</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="min-w-0 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="organization">Organization</Label>
            {orgsLoading ? (
              <div className="text-xs text-muted-foreground">Loading organizations...</div>
            ) : (
              <SelectMenu
                id="organization"
                value={organizationId || undefined}
                onValueChange={setOrganizationId}
                disabled={submitting || orgOptions.length === 0}
                placeholder={
                  orgOptions.length === 0
                    ? 'No organizations available'
                    : 'Select an organization...'
                }
                options={orgOptions}
              />
            )}
            {!orgsLoading && existingOrgIds.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Organizations the user already has a subscription in are excluded.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="permissions">Permission level</Label>
            <Select value={permissions} onValueChange={setPermissions} disabled={submitting}>
              <SelectTrigger id="permissions">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERMISSION_OPTIONS.map(p => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border p-3 bg-muted/40">
            <div className="flex items-start gap-2">
              <EyeOff className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
              <div className="space-y-1">
                <div className="text-sm font-medium">This subscription will be hidden</div>
                <div className="text-xs text-muted-foreground">
                  Hidden subscriptions don&apos;t appear in the customer org&apos;s member list or
                  admin lookup. The user gets access; they just don&apos;t show up as a regular
                  member.
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !organizationId || orgOptions.length === 0}>
              {submitting ? 'Creating...' : 'Create subscription'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
